/* eslint-disable @typescript-eslint/no-explicit-any */
import { useField } from 'formik';
import {
    ChangeEvent,
    KeyboardEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { Form } from 'react-bootstrap';
import AutoSuggestOptions from './AutoSuggestOptions';
import { AutoSuggestOption, AutoSuggestContainerProps } from './types';

const keys = {
    ESC: 27,
    TAB: 9,
    RETURN: 13,
    UP: 38,
    DOWN: 40,
};

// eslint-disable-next-line
const AutoSuggestContainer = <T extends unknown>(
    props: AutoSuggestContainerProps<T>) => {
    const {
        id,
        name,
        label,
        inlineHelp,
        options,
        noResult,
        noResultMessage,
        error,
        errorMessage,
        loading,
        loadingMessage,
        searchTerm,
        onSearchTermChange,
        onSelectedOption,
        onCancel,
        placeholder,
    } = props;

    const helpId = inlineHelp ? `help-${id || name}` : undefined;
    const controlId = `${id || name}`;
    const [_field, _meta, { setTouched }] = useField<string>(name);

    const [isOpen, setIsOpen] = useState(options.length >= 1);
    const [activeOption, setActiveOption] = useState<AutoSuggestOption<T> | undefined>();

    useEffect(() => {
        setIsOpen(options.length >= 1);
    }, [options]);

    const ref = useRef<any>(null);

    const clickListener = useCallback((e: any) => {
        if (!ref?.current?.contains(e.target)) {
            setIsOpen(false);
            onCancel();
        }
    }, [onCancel]);

    useEffect(() => {
        document.addEventListener('click', clickListener);
        return () => document.removeEventListener('click', clickListener);
    }, [clickListener]);

    const onSelectOption = async (value: AutoSuggestOption<T>) => {
        setActiveOption(undefined);
        setIsOpen(false);
        await onSelectedOption(value);
    };

    const moveDown = () => {
        if (isOpen) {
            const option = options.find((x) => x.id === activeOption?.id);
            if (option) {
                const prev = options.indexOf(option);
                if (prev < options.length - 1) {
                    setActiveOption(options[prev + 1]);
                } else {
                    setActiveOption(options[0]);
                }
            } else {
                setActiveOption(options[0]);
            }
        }
    };

    const moveUp = () => {
        if (isOpen) {
            const option = options.find((x) => x.id === activeOption?.id);
            if (option) {
                const prev = options.indexOf(option);
                if (prev > 0) {
                    setActiveOption(options[prev - 1]);
                } else {
                    setActiveOption(options[options.length - 1]);
                }
            }
        }
    };

    const onKeyDown = async (
        event: KeyboardEvent<HTMLInputElement>,
    ) => {
        switch (event.which) {
            case keys.ESC:
                setActiveOption(undefined);
                onCancel();
                break;
            case keys.TAB:
            case keys.RETURN:
                if (activeOption) {
                    event.preventDefault();
                    event.stopPropagation();
                    await onSelectOption(activeOption);
                } else {
                    setActiveOption(undefined);
                    onCancel();
                }
                break;
            case keys.UP:
                event.preventDefault();
                event.stopPropagation();
                moveUp();
                break;
            case keys.DOWN:
                event.preventDefault();
                event.stopPropagation();
                moveDown();
                break;
            default:
                break;
        }
    };

    const onTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        setTouched(true);
        onSearchTermChange(event.target.value);
    };

    return (
        <>
            <div
                id={`${name}-announcement`}
                className='visually-hidden'
                aria-live='polite'
            >
                {
                    isOpen
                        ? `${options.length} suggestions displayed. To navigate, use up and down arrow keys.`
                        : ''
                }
            </div>
            <Form.Group
                controlId={controlId}
                role='combobox'
                tabIndex={-1}
                aria-expanded={isOpen ? 'true' : 'false'}
                // aria-activedescendant={activeOption?.displayText}
                // aria-owns={`${name}-input`}
                aria-haspopup='listbox'
                aria-controls={`${name}-options`}
                className='form-field-container combobox mb-2'
            >
                <Form.Label className={`${name.toLowerCase()}-auto-suggest-label`}>
                    {label}
                </Form.Label>
                {inlineHelp && (
                    <Form.Text as='p' id={helpId} className='contextual-help'>
                        {inlineHelp}
                    </Form.Text>
                )}
                <Form.Control
                    type='text'
                    autoComplete='off'
                    spellCheck='false'
                    aria-autocomplete='both'
                    onChange={onTextChange}
                    onKeyDown={onKeyDown}
                    // onBlur={() => setTouched(true)}
                    value={searchTerm}
                    ref={ref}
                    isInvalid={!!(_meta.touched && _meta.error)}
                    className='search-box form-field'
                    placeholder={placeholder}
                    aria-labelledby={
                        isOpen ? `${name}-autosuggest-options` : undefined
                    }
                    aria-describedby={
                        _meta.touched && _meta.error
                            ? `${id || name}-validation-msg`
                            : (helpId || undefined)
                    }
                />
                {isOpen && (
                    <div
                        className='suggestions-container'
                        id={`${name}-autocomplete`}
                        aria-busy={loading}
                        aria-live='polite'
                    >
                        <AutoSuggestOptions<T>
                            id={`${name}-autosuggest-options`}
                            options={options}
                            onOptionClick={onSelectOption}
                            name={name}
                            selectedOptionId={activeOption?.id}
                        />
                    </div>
                )}
                {loading && <Form.Text>{loadingMessage || 'Loading options'}</Form.Text>}
                {_meta.touched && _meta.error
                    ? (
                        <Form.Control.Feedback type='invalid' id={`${id || name}-validation-msg`}>
                            {_meta.error}
                        </Form.Control.Feedback>
                    )
                    : null}
            </Form.Group>
            {(!loading && noResult) && <Form.Text>{noResultMessage || 'No matches found'}</Form.Text>}
            {error && <Form.Text>{errorMessage || 'Results could not be fetched'}</Form.Text>}
        </>
    );
};

export default AutoSuggestContainer;
